import React, { useState, useEffect, useMemo } from 'react';
import { dataService } from '../../services/dataService';
import { useChatDock } from '../../contexts/ChatDockContext';
import { formatUsd, tierPricing } from '../../utils/agentCost';

/**
 * Every conversation you have had with an agent.
 *
 * This page used to read browser local storage from a chat system that predates
 * agents, so it showed an old support thread and none of the conversations a
 * user had actually had. Conversations live server-side now, one per person per
 * agent, and this lists them — picking one opens it in the dock, which is where
 * talking actually happens.
 */
export default function ChatsPage({ workspaceId, onNavigate, opsMode }) {
  const [chats, setChats] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const { openChat } = useChatDock();

  useEffect(() => {
    if (!workspaceId) { setChats([]); setLoading(false); return; }
    let alive = true;
    dataService.getPipeline(workspaceId)
      .then(p => { if (alive) setNodes(p?.nodes || []); })
      .catch(() => {});
    const unsub = dataService.subscribeAgentChats(workspaceId, c => {
      if (!alive) return;
      setChats(c); setLoading(false);
    });
    return () => { alive = false; unsub(); };
  }, [workspaceId]);

  const teams = useMemo(() => nodes.filter(n => n.type === 'functionNode'), [nodes]);

  /** Agents that exist but have never been spoken to — still worth offering. */
  const unstarted = useMemo(() => {
    const talkedTo = new Set(chats.map(c => `${c.functionNodeId}__${c.agentId}`));
    return teams.flatMap(t =>
      (t.data?.agents || [])
        .filter(a => !talkedTo.has(`${t.id}__${a.id}`))
        .map(a => ({ agent: a, team: t })));
  }, [teams, chats]);

  const teamOf = (id) => teams.find(t => t.id === id);
  const agentOf = (fnId, agentId) =>
    (teamOf(fnId)?.data?.agents || []).find(a => a.id === agentId);

  if (!workspaceId) return <div className="agents-page-empty"><p>Select a workspace.</p></div>;
  if (loading)      return <div className="agents-page-empty"><p>Loading conversations…</p></div>;

  const open = (fnId, agentId) => {
    const team = teamOf(fnId);
    const agent = agentOf(fnId, agentId);
    if (!team || !agent) return;
    openChat({ id: `${fnId}__${agentId}`, agent, fnNode: team, workspaceId });
  };

  return (
    <div className="agents-page">
      <div className="agents-page-header">
        <div>
          <h1 className="agents-page-title">Chats</h1>
          <div className="agents-page-sub">
            Conversations with your {opsMode ? 'employees' : 'agents'}. Opening one docks it at the
            bottom of the screen, so you can keep it while you work.
          </div>
        </div>
      </div>

      {chats.length === 0 && unstarted.length === 0 && (
        <div className="agents-page-empty">
          <div className="agents-empty-icon">💬</div>
          <p>No agents to talk to yet. Add one to a Function node in your pipeline.</p>
          <button className="agents-go-btn" onClick={() => onNavigate('pipeline')}>Go to Pipeline</button>
        </div>
      )}

      {chats.length > 0 && (
        <div className="knowledge-group">
          <div className="knowledge-group-title">Conversations</div>
          {chats
            .slice()
            .sort((a, b) => String(b.lastMessageAt || '').localeCompare(String(a.lastMessageAt || '')))
            .map(c => {
              const agent = agentOf(c.functionNodeId, c.agentId);
              const team  = teamOf(c.functionNodeId);
              return (
                <button
                  key={c.id}
                  className="knowledge-card chats-row"
                  onClick={() => open(c.functionNodeId, c.agentId)}
                  // An agent removed from its team leaves its conversation
                  // behind. Say so rather than opening a window onto nothing.
                  disabled={!agent}
                >
                  <div className="knowledge-card-head">
                    <span className="knowledge-card-title">
                      🤖 {c.agentName || agent?.name || 'Agent'}
                    </span>
                    {c.usdCost > 0 && <span className="chats-cost">{formatUsd(c.usdCost)}</span>}
                  </div>
                  <div className="knowledge-card-meta">
                    {team?.data?.label || 'team no longer exists'}
                    {agent && ` · ${tierPricing(agent.model).label}`}
                    {c.lastMessageAt && ` · last spoke ${new Date(c.lastMessageAt).toLocaleString()}`}
                    {!agent && ' · this agent has been removed'}
                  </div>
                </button>
              );
            })}
        </div>
      )}

      {unstarted.length > 0 && (
        <div className="knowledge-group">
          <div className="knowledge-group-title">Not spoken to yet</div>
          {unstarted.map(({ agent, team }) => (
            <button
              key={`${team.id}__${agent.id}`}
              className="knowledge-card chats-row"
              onClick={() => open(team.id, agent.id)}
            >
              <div className="knowledge-card-head">
                <span className="knowledge-card-title">🤖 {agent.name || 'Agent'}</span>
              </div>
              <div className="knowledge-card-meta">
                {team.data?.label || team.id} · {tierPricing(agent.model).label}
                {agent.role ? ` · ${agent.role}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
